// 📄 src/Component/forms/FormProResubmit.jsx
// Re-soumission du dossier d'inscription PRO (pas les annonces)

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/* ===== Base API robuste (prod & dev) =====
   - Essaie VITE_API_URL / REACT_APP_API_URL
   - Nettoie les slashs finaux + /api éventuel
   - Sinon, retombe sur le même domaine que le front
*/
const RAW =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env.VITE_API_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const API_BASE = (RAW
  ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "")
  : (typeof window !== "undefined" && window.location.origin) || ""
);

const REAPPLY_ENDPOINT = `${API_BASE}/api/users/reapply`; // ✅ route unique côté backend

export default function FormProResubmit({ title = "Renvoyer mon dossier" }) {
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const token  = localStorage.getItem("token");

  const [current, setCurrent]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState({ text: "", type: "" });

  const [formData, setFormData] = useState({
    nom: "", prenom: "", adresse: "", ville: "",
    email: "", password: "", telephone: "", siret: "", role: "pro",
  });
  const [docs, setDocs]         = useState({ kbis: null, id: null, proofAddress: null });
  const [errors, setErrors]     = useState({});

  const emailRegex = /^[\w.-]+@[\w.-]+\.[a-zA-Z]{2,}$/;
  const phoneRegex = /^0\d{9}$/;

  // ----- pré-remplir depuis /api/users/:id -----
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/users/${userId}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        });
        if (res.status === 401) return navigate("/auth/login");
        const u = await res.json();
        if (ignore) return;

        const user = u?.user || u?.data?.user || u;
        setCurrent(user || null);
        setFormData((p) => ({
          ...p,
          nom: user?.nom || "",
          prenom: user?.prenom || "",
          adresse: user?.adresse || "",
          ville: user?.ville || "",
          email: user?.email || "",
          password: "",
          telephone: user?.telephone || "",
          siret: user?.siret || "",
          role: "pro",
        }));
      } catch {
        setMsg({ text: "Erreur de chargement du profil.", type: "error" });
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
    // ✅ API_BASE retiré pour éviter l’avertissement react-hooks/exhaustive-deps
  }, [userId, token, navigate]);

  // ----- validation -----
  const validateFile = (file) => {
    const MAX_SIZE = 10 * 1024 * 1024;
    const allowed  = ["application/pdf", "image/jpeg", "image/png"];
    if (!file) return "";
    if (file.size > MAX_SIZE) return "Taille max 10MB.";
    if (!allowed.includes(file.type)) return "Formats autorisés: PDF/JPG/PNG.";
    return "";
  };

  const handleChange = (e) => {
    setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));
    setErrors((er) => ({ ...er, [e.target.name]: "" }));
  };

  const handleFileChange = (key) => (e) => {
    const file = e.target.files?.[0] || null;
    setDocs((prev) => ({ ...prev, [key]: file }));
    const errKey = key === "proofAddress" ? "doc_proofAddress" : `doc_${key}`;
    setErrors((prev) => ({ ...prev, [errKey]: "" }));
  };

  const validateForm = () => {
    const newErrors = {
      email:      !emailRegex.test(formData.email) ? "Adresse email invalide." : "",
      telephone:  !phoneRegex.test(formData.telephone) ? "Le numéro doit contenir 10 chiffres et commencer par 0." : "",
      adresse:    formData.adresse.trim().length < 5 ? "L'adresse doit contenir au moins 5 caractères." : "",
      ville:      formData.ville.trim().length < 2 ? "La ville est obligatoire." : "",
      password:   formData.password && (formData.password.length < 6 || formData.password.length > 20)
                  ? "Le mot de passe doit contenir entre 6 et 20 caractères." : "",
      siret:      !/^\d{14}$/.test(formData.siret || "") ? "SIRET invalide (14 chiffres)." : "",
      // fichiers facultatifs (uniquement si fournis)
      doc_kbis:         validateFile(docs.kbis),
      doc_id:           validateFile(docs.id),
      doc_proofAddress: validateFile(docs.proofAddress),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const hasChanged = (field) =>
    current ? String(current[field] || "") !== String(formData[field] || "") : true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setMsg({ text: "❌ Veuillez corriger les erreurs dans le formulaire.", type: "error" });
      return;
    }

    try {
      // Vérifs unicité seulement si changement
      if (current) {
        if (hasChanged("telephone")) {
          const r = await fetch(`${API_BASE}/api/auth/check-phone/${formData.telephone}`, {
            credentials: "include",
          });
          const j = await r.json();
          if (j.exists) return setMsg({ text: "❌ Ce numéro de téléphone est déjà utilisé.", type: "error" });
        }
        if (hasChanged("siret")) {
          const r = await fetch(`${API_BASE}/api/auth/check-siret/${formData.siret}`, {
            credentials: "include",
          });
          const j = await r.json();
          if (j.exists) return setMsg({ text: "❌ Ce numéro de SIRET est déjà utilisé.", type: "error" });
        }
      }

      // payload multipart (mêmes noms que l’inscription pro)
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (k === "password" && !v) return; // pas de changement
        fd.append(k, v);
      });
      if (docs.kbis)         fd.append("kbis",         docs.kbis,         docs.kbis.name);
      if (docs.id)           fd.append("idCard",       docs.id,           docs.id.name);
      if (docs.proofAddress) fd.append("proofAddress", docs.proofAddress, docs.proofAddress.name);

      const res = await fetch(REAPPLY_ENDPOINT, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          // ne PAS mettre Content-Type (le navigateur gère le multipart)
        },
        body: fd,
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Une erreur est survenue.");

      setMsg({ text: "✅ Dossier renvoyé. Nous allons revalider vos informations.", type: "success" });
      setTimeout(() => navigate("/pro/profiles/select"), 900);
    } catch (err) {
      setMsg({ text: `❌ Erreur : ${err.message}`, type: "error" });
    }
  };

  if (loading) return <div className="subscribe" style={{ paddingTop: 40 }}>Chargement…</div>;

  const adminNote =
    current?.proReview?.note ||
    current?.commentaireAdmin ||
    "";

  return (
    <div className="subscribe">
      <h2 className="register_title">{title}</h2>

      {adminNote ? (
        <div className="message error" style={{ marginTop: -10, marginBottom: 16 }}>
          <strong>Motif du refus :</strong> {adminNote}
        </div>
      ) : null}

      <form
        className="subscribe_container subscribe_container-pro"
        onSubmit={handleSubmit}
        encType="multipart/form-data"
      >
        <div className="subscribe_container-forms">
          <input className="input_form" name="nom"     placeholder="Nom"     value={formData.nom}     onChange={handleChange} />
          <input className="input_form" name="prenom"  placeholder="Prénom"  value={formData.prenom}  onChange={handleChange} />

          <input
            className={`input_form ${errors.adresse ? "input-error-adresse" : ""}`}
            name="adresse" placeholder="Adresse" value={formData.adresse} onChange={handleChange}
          />
          {errors.adresse && <p className="error-message">{errors.adresse}</p>}

          <input
            className={`input_form ${errors.ville ? "input-error-ville" : ""}`}
            name="ville" placeholder="Ville" value={formData.ville} onChange={handleChange}
          />
          {errors.ville && <p className="error-message">{errors.ville}</p>}

          <input
            className={`input_form ${errors.email ? "input-error-email" : ""}`}
            name="email" placeholder="Email" value={formData.email} onChange={handleChange}
          />
          {errors.email && <p className="error-message">{errors.email}</p>}

          <input
            className={`input_form ${errors.password ? "input-error-password" : ""}`}
            name="password" type="password" placeholder="Mot de passe (laisser vide pour ne pas changer)"
            value={formData.password} onChange={handleChange}
          />
          {errors.password && <p className="error-message">{errors.password}</p>}

          <input
            className={`input_form ${errors.telephone ? "input-error-telephone" : ""}`}
            name="telephone" placeholder="Téléphone" value={formData.telephone} onChange={handleChange}
          />
          {errors.telephone && <p className="error-message">{errors.telephone}</p>}

          <input
            className={`input_form ${errors.siret ? "input-error-siret" : ""}`}
            name="siret" placeholder="Numéro de SIRET (14 chiffres)" value={formData.siret} onChange={handleChange}
          />
          {errors.siret && <p className="error-message">{errors.siret}</p>}

          {/* Fichiers — mêmes libellés/ordres que l’inscription pro */}
          <label className="file_label">
            Extrait Kbis / RNE (≤ 3 mois)
            <input type="file" accept=".pdf,image/png,image/jpeg" onChange={handleFileChange("kbis")} />
          </label>
          {errors.doc_kbis && <p className="error-message">{errors.doc_kbis}</p>}

          <label className="file_label">
            Pièce d’identité du représentant légal
            <input type="file" accept=".pdf,image/png,image/jpeg" onChange={handleFileChange("id")} />
          </label>
          {errors.doc_id && <p className="error-message">{errors.doc_id}</p>}

          <label className="file_label">
            Justificatif d’adresse professionnelle (facture ≤ 3 mois)
            <input type="file" accept=".pdf,image/png,image/jpeg" onChange={handleFileChange("proofAddress")} />
          </label>
          {errors.doc_proofAddress && <p className="error-message">{errors.doc_proofAddress}</p>}

          <button className="input_form_send" type="submit">Renvoyer</button>
          {msg.text && <p className={`message ${msg.type}`}>{msg.text}</p>}
        </div>
      </form>
    </div>
  );
}
